import { Chess } from 'chess.js';
import { useEffect, useMemo, useState } from 'react';
import { Tabuleiro } from './Tabuleiro';

interface EstadoAgente {
  gameId: string;
  version: number;
  humanColor: 'white' | 'black';
  turn: 'white' | 'black';
  status: string;
  fen: string;
  pgn: string;
  lastCpuMove: string | null;
  lastHumanMove: string | null;
  nivel: string;
  inCheck: boolean;
  resultado: string | null;
}

interface Props {
  token: string;
  aoSair: () => void;
}

/**
 * Espetáculo somente leitura da partida controlada pelo agente HTTP.
 * Atualiza sozinho via polling — não move peças.
 */
export function AcompanharAgente({ token, aoSair }: Props) {
  const [estado, setEstado] = useState<EstadoAgente | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [atualizadoEm, setAtualizadoEm] = useState<number | null>(null);
  const [pulso, setPulso] = useState(0);

  useEffect(() => {
    let vivo = true;
    async function carregar() {
      try {
        const r = await fetch(`/api/agent/${encodeURIComponent(token)}`, {
          headers: { Accept: 'application/json' },
          cache: 'no-store'
        });
        const dados = (await r.json().catch(() => ({}))) as EstadoAgente & { mensagem?: string };
        if (!vivo) return;
        if (!r.ok) {
          setErro(dados.mensagem || 'Não foi possível carregar a partida do agente.');
          return;
        }
        setErro(null);
        setEstado((prev) => {
          if (!prev || prev.version !== dados.version || prev.fen !== dados.fen) {
            setPulso((n) => n + 1);
          }
          return dados;
        });
        setAtualizadoEm(Date.now());
      } catch {
        if (vivo) setErro('Falha de rede ao acompanhar o agente.');
      }
    }
    void carregar();
    const timer = window.setInterval(() => void carregar(), 800);
    return () => {
      vivo = false;
      window.clearInterval(timer);
    };
  }, [token]);

  const jogo = useMemo(() => {
    const c = new Chess();
    if (estado?.fen) {
      try {
        c.load(estado.fen);
      } catch {
        /* posição inválida — mantém inicial */
      }
    }
    return c;
  }, [estado?.fen, pulso]);

  const ultima = useMemo(() => {
    const uci = estado?.lastCpuMove || estado?.lastHumanMove;
    if (!uci || uci.length < 4) return undefined;
    return { de: uci.slice(0, 2), para: uci.slice(2, 4) };
  }, [estado?.lastCpuMove, estado?.lastHumanMove]);

  const orientacao = estado?.humanColor === 'black' ? 'b' : 'w';
  const corHumano = estado?.humanColor === 'black' ? 'pretas' : 'brancas';
  const vez = estado?.turn === 'black' ? 'pretas' : 'brancas';

  return (
    <div className="tela-partida acompanhar-agente">
      <header className="barra-partida barra-acompanhar">
        <button type="button" className="botao-contorno" onClick={aoSair}>
          Voltar
        </button>
        <div className="estado-barra">
          <strong>Acompanhando agente</strong>
          {estado ? (
            <span>
              · {estado.gameId} · v{estado.version} · {estado.status}
              {estado.inCheck ? ' · xeque' : ''}
            </span>
          ) : null}
          <i className="ponto-vivo" title="Atualização automática" />
          {atualizadoEm ? (
            <small className="meta-atualizacao">
              ao vivo · {new Date(atualizadoEm).toLocaleTimeString('pt-BR')}
            </small>
          ) : null}
        </div>
        <nav>
          <a className="botao-contorno" href={`/agent/${encodeURIComponent(token)}/live`}>
            Tela live (servidor)
          </a>
        </nav>
      </header>

      {erro ? <p className="mensagem-erro painel-acompanhar-msg">{erro}</p> : null}

      {!estado && !erro ? (
        <p className="painel-acompanhar-msg">Carregando partida do agente…</p>
      ) : null}

      {estado ? (
        <section className="mesa-partida mesa-acompanhar">
          <div className="area-tabuleiro">
            <div className="placa-estado placa-acompanhar">
              <span>Humano: {corHumano}</span>
              <span>Vez: {vez}</span>
              <span>CPU: {estado.nivel}</span>
              {estado.resultado ? <span className="resultado-acompanhar">{estado.resultado}</span> : null}
            </div>

            {/* Wrapper com largura + aspect-ratio explícitos — evita tabuleiro com altura 0 */}
            <div className="envoltorio-tabuleiro-eval envoltorio-acompanhar">
              <Tabuleiro
                key={`${estado.gameId}-${estado.version}-${estado.fen}`}
                jogo={jogo}
                orientacao={orientacao}
                selecionada={null}
                destinos={[]}
                ultimaJogada={ultima}
                desabilitado
                mostrarDestinos={false}
                aoSelecionar={() => undefined}
                aoMover={() => undefined}
              />
            </div>

            <div className="faixa-lances-acompanhar">
              <div>
                <small>Último humano</small>
                <strong>{estado.lastHumanMove ?? '—'}</strong>
              </div>
              <div>
                <small>Última CPU</small>
                <strong>{estado.lastCpuMove ?? '—'}</strong>
              </div>
            </div>
          </div>

          <aside className="painel-partida painel-acompanhar">
            <header>
              <small>PARTIDA {estado.gameId}</small>
              <h2>Estado ao vivo</h2>
            </header>
            <pre className="bloco-fen-acompanhar">{estado.fen}</pre>
            <div className="bloco-pgn-acompanhar">
              <small>PGN</small>
              <pre>{estado.pgn}</pre>
            </div>
            <p className="dica-acompanhar">
              Esta tela só observa. O controle é pela rota <code>/agent/…</code>.
              Atualiza a cada ~0,8 s.
            </p>
          </aside>
        </section>
      ) : null}
    </div>
  );
}
